import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Typography,
  Popconfirm,
  Tag,
  Drawer,
  DatePicker,
  Select,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  CopyOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import apiTokenService from '../../services/apiTokenService';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const ApiSettingsPage = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [tokenModalVisible, setTokenModalVisible] = useState(false);
  const [logsDrawerVisible, setLogsDrawerVisible] = useState(false);
  const [newToken, setNewToken] = useState('');
  const [selectedTokenId, setSelectedTokenId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [form] = Form.useForm();

  // ดึงรายการ tokens
  const fetchTokens = async () => {
    setLoading(true);
    try {
      const response = await apiTokenService.getApiTokens();
      setTokens(response.data);
    } catch (error) {
      message.error('ไม่สามารถโหลดรายการ API Tokens ได้');
    } finally {
      setLoading(false);
    }
  };

  // ดึง logs ของ token
  const fetchLogs = async (tokenId) => {
    setLogsLoading(true);
    try {
      const response = await apiTokenService.getApiTokenLogs(tokenId);
      setLogs(response.data.logs);
    } catch (error) {
      message.error('ไม่สามารถโหลด logs ได้');
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchTokens();
  }, []);

  // สร้าง token ใหม่
  const handleCreateToken = async (values) => {
    try {
      const response = await apiTokenService.createApiToken(values.appName);
      setNewToken(response.data.token);
      setCreateModalVisible(false);
      setTokenModalVisible(true);
      form.resetFields();
      fetchTokens();
      message.success('สร้าง API Token สำเร็จ');
    } catch (error) {
      message.error('ไม่สามารถสร้าง API Token ได้');
    }
  };

  // Regenerate token
  const handleRegenerateToken = async (id) => {
    try {
      const response = await apiTokenService.regenerateApiToken(id);
      setNewToken(response.data.token);
      setTokenModalVisible(true);
      fetchTokens();
      message.success('สร้าง Token ใหม่สำเร็จ');
    } catch (error) {
      message.error('ไม่สามารถสร้าง Token ใหม่ได้');
    }
  };

  // ลบ token
  const handleDeleteToken = async (id) => {
    try {
      await apiTokenService.deleteApiToken(id);
      fetchTokens();
      message.success('ลบ API Token สำเร็จ');
    } catch (error) {
      message.error('ไม่สามารถลบ API Token ได้');
    }
  };

  // คัดลอก token
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success('คัดลอกไปยังคลิปบอร์ดแล้ว');
  };

  // แสดง logs
  const handleViewLogs = (tokenId) => {
    setSelectedTokenId(tokenId);
    setLogsDrawerVisible(true);
    fetchLogs(tokenId);
  };

  // Columns สำหรับตาราง tokens
  const tokenColumns = [
    {
      title: 'ชื่อแอปพลิเคชัน',
      dataIndex: 'app_name',
      key: 'app_name',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Token ID',
      dataIndex: 'token_jti',
      key: 'token_jti',
      render: (text) => <Text code>{text?.substring(0, 16)}...</Text>,
    },
    {
      title: 'สถานะ',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) =>
        isActive ? (
          <Tag color="green">ใช้งานอยู่</Tag>
        ) : (
          <Tag color="red">ไม่ใช้งาน</Tag>
        ),
    },
    {
      title: 'วันที่สร้าง',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'ใช้งานล่าสุด',
      dataIndex: 'last_used_at',
      key: 'last_used_at',
      render: (date) => (date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'),
    },
    {
      title: 'การจัดการ',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewLogs(record.id)}
          >
            ดู Logs
          </Button>
          <Popconfirm
            title="คุณแน่ใจหรือไม่ว่าต้องการสร้าง Token ใหม่?"
            description="Token เดิมจะไม่สามารถใช้งานได้อีกต่อไป"
            onConfirm={() => handleRegenerateToken(record.id)}
            okText="ยืนยัน"
            cancelText="ยกเลิก"
          >
            <Button type="link" icon={<ReloadOutlined />}>
              สร้างใหม่
            </Button>
          </Popconfirm>
          <Popconfirm
            title="คุณแน่ใจหรือไม่ว่าต้องการลบ Token นี้?"
            onConfirm={() => handleDeleteToken(record.id)}
            okText="ยืนยัน"
            cancelText="ยกเลิก"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              ลบ
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Columns สำหรับตาราง logs
  const logColumns = [
    {
      title: 'เวลา',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      title: 'Method',
      dataIndex: 'method',
      key: 'method',
      width: 100,
      render: (method) => (
        <Tag color={method === 'GET' ? 'blue' : 'orange'}>{method}</Tag>
      ),
    },
    {
      title: 'Endpoint',
      dataIndex: 'endpoint',
      key: 'endpoint',
      render: (text) => <Text code>{text}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'response_status',
      key: 'response_status',
      width: 100,
      render: (status) => (
        <Tag color={status >= 200 && status < 300 ? 'success' : 'error'}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 150,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Title level={3}>การจัดการ API Tokens</Title>
          <Paragraph type="secondary">
            สร้างและจัดการ API Tokens สำหรับแอปพลิเคชันภายนอก Token
            จะไม่มีวันหมดอายุและจะหมดอายุเฉพาะเมื่อมีการสร้างใหม่เท่านั้น
          </Paragraph>
        </div>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateModalVisible(true)}
          style={{ marginBottom: 16 }}
        >
          สร้าง Token ใหม่
        </Button>

        <Table
          columns={tokenColumns}
          dataSource={tokens}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Modal: สร้าง Token ใหม่ */}
      <Modal
        title="สร้าง API Token ใหม่"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateToken}>
          <Form.Item
            label="ชื่อแอปพลิเคชัน"
            name="appName"
            rules={[
              {
                required: true,
                message: 'กรุณากรอกชื่อแอปพลิเคชัน',
              },
            ]}
          >
            <Input placeholder="เช่น Mobile App, Third-party Service" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                สร้าง Token
              </Button>
              <Button onClick={() => setCreateModalVisible(false)}>
                ยกเลิก
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal: แสดง Token */}
      <Modal
        title="API Token ของคุณ"
        open={tokenModalVisible}
        onCancel={() => setTokenModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setTokenModalVisible(false)}>
            ปิด
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="warning" strong>
            ⚠️ กรุณาคัดลอกและเก็บ Token นี้ไว้ในที่ปลอดภัย คุณจะไม่สามารถกลับมาดู Token นี้ได้อีกครั้ง
          </Text>
        </div>
        <Input.TextArea
          value={newToken}
          readOnly
          rows={4}
          style={{ marginBottom: 16 }}
        />
        <Button
          type="primary"
          icon={<CopyOutlined />}
          onClick={() => copyToClipboard(newToken)}
          block
        >
          คัดลอก Token
        </Button>
      </Modal>

      {/* Drawer: แสดง Logs */}
      <Drawer
        title="ประวัติการใช้งาน API"
        width={900}
        open={logsDrawerVisible}
        onClose={() => setLogsDrawerVisible(false)}
      >
        <Table
          columns={logColumns}
          dataSource={logs}
          rowKey="id"
          loading={logsLoading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 800 }}
        />
      </Drawer>
    </div>
  );
};

export default ApiSettingsPage;
